import { Fragment, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import PulseLoader from "../Loader/PulseLoader";
import Button from "../FormFields/Button";
import { parentScrollToTheTop } from "../../lib/utils";
import useAutosize from "../../lib/use_autosize";

interface DialogProps {
  cancelLabel?: string;
  confirmLabel?: string;
  message?: string;
  noCancel?: boolean;
  noOk?: boolean;
  onConfirm: () => void;
  onHide: () => void;
  show: boolean;
  title?: string;
  zIndexClass?: string;
}

export default function ConfirmationDialog({
  cancelLabel,
  confirmLabel,
  message,
  noCancel = false,
  noOk = false,
  onConfirm,
  onHide,
  show = false,
  title,
  zIndexClass = "z-[999]",
}: DialogProps) {
  const [loading, setLoading] = useState(false);
  const [parentScroll, setParentScroll] = useState(0);
  const loader = <PulseLoader colorVar="primary-text" size={1.5} />;

  const dialogRef = useRef();
  const [setElement] = useAutosize({
    label: "confirmation",
    enabled: show,
    dontResizeOnZeroHeight: true,
  });

  useEffect(() => {
    if (parentScroll == 0) {
      setParentScroll(1);
      return;
    } else parentScrollToTheTop();
  }, [show]);

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog
        as="div"
        key={show.toString()}
        className={`relative ${zIndexClass}`}
        onClose={() => !loading && onHide()}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div
            className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
            ref={(el) => {
              dialogRef.current = el;
              setElement(el);
            }}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="autosize relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon
                      className="h-6 w-6 text-red-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-start sm:mt-0 sm:ml-4 sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      {title}
                    </Dialog.Title>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">{message}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  {!noOk && (
                    <Button
                      color="red"
                      type="button"
                      className="sm:ml-5"
                      onClick={async () => {
                        setLoading(true);
                        await onConfirm();
                        await onHide();
                        setLoading(false);
                      }}
                    >
                      {loading ? loader : confirmLabel}
                    </Button>
                  )}
                  {!noCancel && (
                    <Button
                      color="transparent"
                      type="button"
                      className="mt-5 sm:mt-0"
                      onClick={() => onHide()}
                    >
                      {cancelLabel}
                    </Button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
